import { ErrorMessage, Field, Form as FormikForm, Formik, FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { Form, FormControl, Image, Stack } from 'react-bootstrap';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import SFCLogoMD from '../../assets/images/sticky-fingers-logo-stacked-2.png';
import { SimpleButton } from '../../components/buttons/SimpleButton';
import { PasswordField } from '../../components/inputs/PasswordField';
import {
  useLoginMutation,
  useMfaVerifyMutation,
  useMfaEnrollStartMutation,
  useMfaEnrollConfirmMutation
} from '../../services/endpoints/people/user';
import {
  isMfaChallenge,
  isMfaEnrollment
} from '../../services/endpoints/people/user';
import { isAuthenticated } from '../../services/helper';
import { PageMeta } from '../../components/meta/PageMeta';
import { getSafeReturnTo } from '../../utils/returnTo';
import { getErrorMessage } from '../../utils/utils';
import { FadeIn } from '../animations/FadeIn';

type Props = {
  isModal?: boolean;
};
interface FormValues {
  email: string;
  password: string;
}

type Phase = 'password' | 'challenge' | 'enroll';

const Login: React.FC<Props> = ({ isModal }) => {
  const navigate: NavigateFunction = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));

  const goAfterLogin = () => {
    navigate(returnTo ?? '/');
  };

  useEffect(() => {
    if (isAuthenticated()) {
      goAfterLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [login] = useLoginMutation();
  const [mfaVerify] = useMfaVerifyMutation();
  const [mfaEnrollStart] = useMfaEnrollStartMutation();
  const [mfaEnrollConfirm] = useMfaEnrollConfirmMutation();

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  // MFA flow state. pendingToken is held in memory only, never localStorage.
  const [phase, setPhase] = useState<Phase>('password');
  const [pendingToken, setPendingToken] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [manualKey, setManualKey] = useState<string>('');

  const initialValues: FormValues = {
    email: '',
    password: ''
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('This field is required!'),
    password: Yup.string().min(6, 'Password is too short').required('This field is required!')
  });

  const handleSubmit = async (formValue: FormValues) => {
    const { email: username, password } = formValue;
    setLoading(true);
    setMessage('');

    try {
      const res = await login({ username, password }).unwrap();

      if (isMfaChallenge(res)) {
        setPendingToken(res.pendingToken);
        setPhase('challenge');
        return;
      }

      if (isMfaEnrollment(res)) {
        setPendingToken(res.pendingToken);
        try {
          const enroll = await mfaEnrollStart({ pendingToken: res.pendingToken }).unwrap();
          setQrDataUrl(enroll.qrDataUrl);
          setManualKey(enroll.manualEntryKey);
          setPhase('enroll');
        } catch (error) {
          setMessage(getErrorMessage(error));
        }
        return;
      }

      // Real session: login mutation did not store it (no transformResponse),
      // but mfaVerify/enrollConfirm do. A plain logged-in response here means a
      // non-eligible user; persist via the same path the app expects.
      localStorage.setItem('user', JSON.stringify(res.user));
      goAfterLogin();
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    setMessage('');
    try {
      await mfaVerify({ pendingToken, token: code }).unwrap();
      goAfterLogin();
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollConfirm = async () => {
    setLoading(true);
    setMessage('');
    try {
      await mfaEnrollConfirm({ pendingToken, token: code }).unwrap();
      goAfterLogin();
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const codeInput = (
      <Form.Group className="mb-3" controlId="mfaCode">
        <Form.Label>6-digit code</Form.Label>
        <Form.Control
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, ''))}
            placeholder="123456"
        />
      </Form.Group>
  );

  return (
      <FadeIn className="d-flex flex-column align-content-center flex-wrap">
        <PageMeta title="Log In" />
        {!isModal && (
            <>
              <div className="pb-4">
                <Image
                    fluid
                    className="d-block d-sm-none"
                    src={SFCLogoMD}
                    alt="Sticky Fingers Logo"
                    onClick={() => navigate('/')}
                />
                <Image
                    className="d-none d-sm-inline-flex"
                    src={SFCLogoMD}
                    style={{ maxWidth: 343 }}
                    alt="Sticky Fingers Logo"
                    onClick={() => navigate('/')}
                />
              </div>

              <h2
                  className="text-dark fw-bold mt-lg-5 text-md-center mt-4 text-start"
                  style={{ fontSize: 32 }}
              >
                {phase === 'password' && 'Welcome Back!'}
                {phase === 'challenge' && 'Verify it’s you'}
                {phase === 'enroll' && 'Set up two-factor authentication'}
              </h2>
            </>
        )}
        <Stack className="w-100" style={{ maxWidth: 480 }} gap={1}>
          {phase === 'password' && (
              <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
              >
                {({ submitForm, errors, touched }: FormikProps<FormValues>) => (
                    <FormikForm className="text-start">
                      <div className={!isModal ? 'mb-5 pb-5' : 'mb-3 pb-3'}>
                        <Form.Group className="mb-2" controlId="email">
                          <Form.Label>Email Address</Form.Label>
                          <Field
                              name="email"
                              type="email"
                              as={Form.Control}
                              isInvalid={touched.email && !!errors.email}
                          />
                          <ErrorMessage name="email">
                            {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                          </ErrorMessage>
                        </Form.Group>

                        <PasswordField name="password" label="Password" groupClassName="mb-2" />

                        <div className="d-flex justify-content-between align-items-center">
                          <Form.Group className="fw-semibold" controlId="rememberMe">
                            <Form.Check type="checkbox" label="Remember me" />
                          </Form.Group>
                          <button
                              type="button"
                              onClick={() => navigate('/users/forgot-password')}
                              className="btn btn-link fs-small fw-bolder text-uppercase link-primary link-underline-opacity-0 p-0"
                          >
                            Forgot Password?
                          </button>
                        </div>
                      </div>

                      {message && (
                          <div className="form-group">
                            <div className="alert alert-danger" role="alert">
                              {message}
                            </div>
                          </div>
                      )}

                      <SimpleButton
                          type="button"
                          className="w-100"
                          variant="primary"
                          disabled={loading}
                          onClick={submitForm}
                      >
                        {loading ? <span className="spinner-border spinner-border-sm"></span> : 'Log In'}
                      </SimpleButton>
                    </FormikForm>
                )}
              </Formik>
          )}

          {phase === 'challenge' && (
              <div className="text-start">
                <p className="text-secondary mb-3">
                  Enter the 6-digit code from your authenticator app.
                </p>
                {codeInput}
                {message && (
                    <div className="alert alert-danger" role="alert">
                      {message}
                    </div>
                )}
                <SimpleButton
                    type="button"
                    className="w-100"
                    variant="primary"
                    disabled={loading || code.length < 6}
                    onClick={handleVerify}
                >
                  {loading ? <span className="spinner-border spinner-border-sm"></span> : 'Verify'}
                </SimpleButton>
              </div>
          )}

          {phase === 'enroll' && (
              <div className="text-start">
                <p className="text-secondary mb-3">
                  Your account requires two-factor authentication. Scan this code with an
                  authenticator app (Google Authenticator, Authy, 1Password), then enter the
                  6-digit code it shows.
                </p>
                {qrDataUrl && (
                    <div className="d-flex justify-content-center mb-3">
                      <Image src={qrDataUrl} alt="MFA QR code" style={{ width: 200, height: 200 }} />
                    </div>
                )}
                {manualKey && (
                    <p className="text-secondary fs-small mb-3">
                      Can’t scan? Enter this key manually: <code>{manualKey}</code>
                    </p>
                )}
                {codeInput}
                {message && (
                    <div className="alert alert-danger" role="alert">
                      {message}
                    </div>
                )}
                <SimpleButton
                    type="button"
                    className="w-100"
                    variant="primary"
                    disabled={loading || code.length < 6}
                    onClick={handleEnrollConfirm}
                >
                  {loading ? (
                      <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                      'Confirm and continue'
                  )}
                </SimpleButton>
              </div>
          )}

          {!isModal && phase === 'password' && (
              <div className="d-flex justify-content-center align-items-center my-3">
                <span className="ff-inter fs-small text-secondary">Don't have an account?</span>
                <button
                    onClick={() => navigate('/users/register')}
                    className="btn btn-link fs-small fw-bolder text-uppercase link-primary link-underline-opacity-0 ms-2 p-0"
                >
                  Sign Up
                </button>
              </div>
          )}
        </Stack>
      </FadeIn>
  );
};

export default Login;